// import extend function - to add custom rules in vee-validate
import { extend } from "vee-validate";
import { changeTimeToSeconds, checkTimeBetweenTwoTimes } from "./helper";
import i18n from "./translations/index";

// import the already available rules
import {
  required,
  max,
  email,
  min,
  min_value,
  numeric,
  max_value,
} from "vee-validate/dist/rules";

// ---- existing rules ----

// required field validation
export const requiredField = extend("required", {
  ...required,
  message: (field) => i18n.t("fieldRequired").replace("{_field_}", field),
});

export const emailIdField = extend("email", {
  ...email,
  message: () => i18n.t("validEmailRequired"),
});

export const numericField = extend("numeric", {
  ...numeric,
  message: () => i18n.t("onlyNumericsAllowed"),
});

export const maxVal = extend("max", max);

export const minVal = extend("min", min);

export const minInteger = extend("min_value", min_value);

export const maxInteger = extend("max_value", max_value);

/* ---- custom rules ---- */

// maximum value validation with customizing message
export const maxIntegerValidation = extend("maximum", (value, args) => {
  if (parseInt(value, 10) <= parseInt(args[0], 10)) {
    return true;
  }
  return args && args[1] // when custom msg is available we can present that
    ? args[1] // custom msg
    : i18n.t("valueLessThanOrEqual").replace("{max}", args[0]); // default msg
});

// minimum value validation with customizing message
export const minIntegerValidation = extend("minimum", (value, args) => {
  if (parseInt(value, 10) >= parseInt(args[0], 10)) {
    return true;
  }
  return i18n.t("valueGreaterThanOrEqual").replace("{min}", args[0]);
});

export const notSameValidation = extend("notSame", (value, args) => {
  if (value !== args[0]) {
    return true;
  }
  return args[1];
});

// maximum float value validation with customizing message
export const maxFloatValidation = extend("maxFloat", (value, args) => {
  // args[0]: maximum number to be compared with value
  // args[1]: custom message
  if (parseFloat(value) <= parseFloat(args[0])) {
    return true;
  }
  return args && args[1] // when custom msg is available we can present that
    ? args[1] // custom msg
    : i18n.t("valueLessThanOrEqual").replace("{max}", args[0]); // default msg
});

// minimum float value validation with customizing message
export const minFloatValidation = extend("minFloat", (value, args) => {
  // args[0]: minimum number to be compared with value
  // args[1]: custom message
  if (parseFloat(value) >= parseFloat(args[0])) {
    return true;
  }
  let argsAsString =
    parseInt(args, 10) === 0
      ? i18n.t("valueGreaterThanOrEqualZero")
      : i18n.t("valueGreaterThanOrEqual").replace("{min}", args[0]);
  return args && args[1] // when custom msg is available we can present that
    ? args[1] // custom msg
    : argsAsString; // default msg
});

// max length validation
export const maxLengthValidation = extend("max", (value, args) => {
  if (value.length <= args.length) {
    return true;
  }
  return i18n.t("maxCharactersExceeded").replace("{max}", args.length);
});

// min length validation
export const minLengthValidation = extend("min", (value, args) => {
  if (value.length >= args.length) {
    return true;
  }
  return i18n.t("minCharactersRequired").replace("{min}", args.length);
});

// Only numeric validation
export const onlyNumValidation = extend("onlyNum", {
  validate: (value) => /^[0-9]+$/.test(value),
  message: () => i18n.t("onlyNumericsAllowed"),
});
// alphabets, space and dots are allowed
export const alphaSpaceDotValidation = extend("alphaSpaceDot", (value) => {
  if (/^[a-zA-Z.\s]+$/.test(value)) {
    return true;
  }
  return i18n.t("onlyAlphabetsSpaceDotsAllowed");
});

// number with two decimal
export const numberWithTwoDecimalValidation = extend(
  "numberWithTwoDecimal",
  (value) => {
    if (/^\d{1,13}(\.\d{0,2})?$/.test(value)) {
      return true;
    }
    return i18n.t("numericTwoDecimalAllowed");
  }
);

// alphabets, space, hyphen, underscore and dots are allowed
export const alphaNumSpaceDotHyphenQuotesValidation = extend(
  "alphaNumSpaceDotHyphenQuotes",
  (value) => {
    if (/^[a-zA-Z0-9'.\-_\s]+$/i.test(value)) {
      return true;
    }
    return i18n.t("alphaNumSpaceDotHyphenQuotesAllowed");
  }
);

// alphanumeric, spaces and symbols(+ , / . # & : () ' - ) are allowed
export const commentValidation = extend("vComment", (value) => {
  if (/^[\w\.\,\#\+\&\/\-\(\)\:\'\ ]*$/.test(value)) {
    return true;
  }
  return i18n.t("commentFormatAllowed");
});

// check the passed argument is greater than the time value
export const greaterThanTimeValidation = extend(
  "greaterThanTime",
  (value, args) => {
    let valueInSecs = changeTimeToSeconds(value);
    let argsInSecs = changeTimeToSeconds(args[0]);
    if (valueInSecs > argsInSecs) {
      return true;
    }
    return args[1]; // custom message;
  }
);

// check the passed argument is less than the time value
export const lessThanTimeValidation = extend("lessThanTime", (value, args) => {
  let valueInSecs = changeTimeToSeconds(value);
  let argsInSecs = changeTimeToSeconds(args[0]);
  if (valueInSecs < argsInSecs) {
    return true;
  }
  return args[1]; // custom message;
});

// check the passed value is between passed argument times
export const betweenTimeValidation = extend("betweenTime", (value, args) => {
  let isBetween = checkTimeBetweenTwoTimes(value, args[0], args[1]);
  if (isBetween) {
    return true;
  }
  return args[2]; // custom message;
});

// alphanumeric, spaces and symbols(, () )  are allowed
export const alphaNumSpaceWithTwoSymbolValidation = extend(
  "alphaNumSpaceWithTwoSymbol",
  (value) => {
    if (/^[\w\(\)\,\ ]*$/.test(value)) {
      return true;
    }
    return i18n.t("alphaNumSpaceSymbolsAllowed");
  }
);

//Only alphanumeric, space and symbols ( . , # + & / - ( ) : ` ' ? ) are allowed
export const alphaNumSpCDotHySlashValidation = extend(
  "alphaNumSpCDotHySlashValidation",
  (value) => {
    if (/^[a-zA-Z0-9\.\,\#\+\&\/\-\(\)\:\`\'\?\ ]*$/.test(value)) {
      return true;
    }
    return i18n.t("alphaNumSpaceExtendedSymbolsAllowed");
  }
);

// Blocks angle brackets only; allows all other characters.
export const commonInputAlphaNumericValidation = extend(
  "commonInputAlphaNumericValidation",
  (value) => {
    if (/^[^<>]*$/u.test(value)) {
      return true;
    }
    return i18n.t("commonInputAlphaNumericValidationMessage");
  }
);

//numbers space and hyphen
export const numSpaceWithHyphenValidation = extend(
  "numSpaceHyphen",
  (value) => {
    if (/^(?=.*[0-9])[- 0-9]+$/.test(value)) {
      return true;
    }
    return i18n.t("numSpaceHyphenAllowed");
  }
);

// mobile & telephone number validation
export const mobileNoValidation = extend("mobileNo", (value) => {
  if (
    /^([\+][0-9]{1,3}[ \.\-])?([\(]{1}[0-9]{2,6}[\)])?([0-9 \+\.\-\/]{5,15})((x|ext|extension)[ ]?[0-9]{1,4})?$/.test(
      value
    )
  ) {
    return true;
  }
  return i18n.t("validMobileRequired");
});

// alphanumeric, spaces and symbols(- , ( ) )  are allowed
export const alphaNumSpaceWithFourSymbolValidation = extend(
  "alphaNumSpaceWithFourSymbol",
  (value) => {
    if (/^[\w\-\(\)\,\ ]*$/.test(value)) {
      return true;
    }
    return i18n.t("alphaNumSpaceFourSymbolsAllowed");
  }
);

// alphabets, space are allowed
export const alphaNumHySpaceValidation = extend("alphaNumHySpace", (value) => {
  if (/^[\w\-\ ]*$/.test(value)) {
    return true;
  }
  return i18n.t("alphaNumSpaceHyphenAllowed");
});

// alphanumeric, spaces and symbols(& , () . )  are allowed.
export const onlyLetterSpDotAmpBracketValidation = extend(
  "onlyLetterSpDotAmpBracket",
  (value) => {
    if (/^[\w\.\&\,\(\)\ ]+$/.test(value)) {
      return true;
    }
    return i18n.t("alphaNumSpaceAmpBracketAllowed");
  }
);

// alphanumeric, spaces and symbols(& , () . )  are allowed.
export const designationValidation = extend("vDesignation", (value) => {
  if (/^[a-zA-Z0-9\|\\_\\.\,\#\+\&\/\-\(\)\:\`\'\ ]*$/.test(value)) {
    return true;
  }
  return i18n.t("designationFormatAllowed");
});

export const titleValidation = extend("vTitle", (value) => {
  if (/^[\w\.\,\#\+\&\/\-\(\)\:\`\'\ ]*$/.test(value)) {
    return true;
  }
  return i18n.t("titleFormatAllowed");
});

// letters, numbers, spaces, and certain special characters are allowed
export const descriptionValidation = extend(
  "descriptionValidation",
  (value) => {
    if (/^[\w\s\d.,!@%^&*-_+=;:'"<>?/\()|`~’\n]+$/.test(value)) {
      return true;
    }
    return i18n.t("descriptionFormatAllowed");
  }
);

export const domainNameValidation = extend("vDomainName", (value) => {
  const domainPattern =
    /^([a-zA-Z0-9](?:[a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
  if (domainPattern.test(value)) {
    return true;
  }
  return i18n.t("validDomainRequired");
});

export const alphaNumSpaceWithSymbolsValidation = extend(
  "alphaNumSpaceWithSymbols",
  (value) => {
    if (/^[a-zA-Z0-9\s&+,/#:()'.-]/.test(value)) {
      return true;
    }
    return i18n.t("alphaNumSpaceBasicSymbolsAllowed");
  }
);

// alphabets and numerics are allowed
export const alphaNumericValidation = extend("alphaNumeric", (value) => {
  if (/^[a-zA-Z0-9]*$/.test(value)) {
    return true;
  }
  return i18n.t("onlyAlphaNumericAllowed");
});

export const bsbCodeValidation = extend("bsbCode", (value) => {
  if (/\d{3}-?\d{3}/.test(value)) {
    return true;
  }
  return i18n.t("validBSBRequired");
});

//IFSC validation
export const ifscValidation = extend("ifscCode", (value) => {
  if (/^[A-Z]{4}[0][A-Z0-9]{6}$/.test(value)) {
    return true;
  }
  return i18n.t("validIFSCRequired");
});
